import { configWithUserToken } from "../../services/config";
import {
  ChangeRequestStatus, IChangeRequestQueue, IEvidence, IIncident, IIncidentHistory, IncidentStatus, IncidentType,
  IOrderIncidents, IPool, IRestaurantIncidents,
} from "../models/IIncident";

const BASE = "CustomerIncident";

/* ───────── Bildirim aç (yönetici / personel / atanmış kurye) ───────── */

/** Her gönderimde yeni bir Idempotency-Key; "tekrar dene" aynı anahtarı kullanmalı. */
export const newIdempotencyKey = (): string =>
  (typeof crypto !== "undefined" && "randomUUID" in crypto)
    ? crypto.randomUUID()
    : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });

export const createIncident = (orderId: string, type: IncidentType, description: string, idempotencyKey: string) =>
  configWithUserToken.post<IIncident>(BASE, { orderId, type, description }, { headers: { "Idempotency-Key": idempotencyKey } });

export const getOrderIncidents = (orderId: string) =>
  configWithUserToken.get<IOrderIncidents>(`${BASE}/order/${orderId}`);

export const getRestaurantIncidents = (restaurantId: string, status?: IncidentStatus, pageIndex = 0, pageSize = 20) => {
  const p = new URLSearchParams({ pageIndex: String(pageIndex), pageSize: String(pageSize) });
  if (status) p.set("status", status);
  return configWithUserToken.get<IRestaurantIncidents>(`${BASE}/restaurant/${restaurantId}?${p.toString()}`);
};

/** Yalnız bildirimin restoranının yöneticisi; yalnız PendingConfirmation için */
export const setIncidentStatus = (id: string, status: "Confirmed" | "Rejected") =>
  configWithUserToken.put<IIncident>(`${BASE}/${id}/status`, { status });

/* ───────── Kanıt görseli ───────── */

export const uploadEvidence = (id: string, file: File) => {
  const form = new FormData();
  form.append("image", file);
  return configWithUserToken.post<IEvidence>(`${BASE}/${id}/evidence`, form, { headers: { "Content-Type": "multipart/form-data" } });
};

/** URL 15 dk geçerli — saklamayın, göstermeden hemen önce alın */
export const getEvidence = (id: string) => configWithUserToken.get<IEvidence>(`${BASE}/${id}/evidence`);

/* ───────── SuperAdmin ───────── */

export const getAdminIncident = (id: string) => configWithUserToken.get<IIncidentHistory>(`${BASE}/admin/${id}`);

export const getChangeRequests = (status?: ChangeRequestStatus, pageIndex = 0, pageSize = 20) => {
  const p = new URLSearchParams({ pageIndex: String(pageIndex), pageSize: String(pageSize) });
  if (status) p.set("status", status);
  return configWithUserToken.get<IChangeRequestQueue>(`${BASE}/admin/change-requests?${p.toString()}`);
};

export const decideChangeRequest = (requestId: string, approve: boolean, reason: string) =>
  configWithUserToken.put<IIncidentHistory>(`${BASE}/admin/change-requests/${requestId}/decision`, { approve, reason });

export const adminCorrect = (id: string, body: { type?: IncidentType; description?: string; reason: string }) =>
  configWithUserToken.put<IIncidentHistory>(`${BASE}/admin/${id}`, body);

export const adminRemove = (id: string, reason: string) =>
  configWithUserToken.put<IIncidentHistory>(`${BASE}/admin/${id}/remove`, { reason });

export const adminDispute = (id: string, reason: string) =>
  configWithUserToken.put<IIncidentHistory>(`${BASE}/admin/${id}/dispute`, { reason });

export const adminRestore = (id: string, reason: string) =>
  configWithUserToken.put<IIncidentHistory>(`${BASE}/admin/${id}/restore`, { reason });

/* ───────── Paylaşım havuzu (SuperAdmin) ───────── */

export const getPool = () => configWithUserToken.get<IPool>("CustomerIncidentPool/members");
export const addPoolMembers = (ids: string[]) =>
  configWithUserToken.put<IPool>("CustomerIncidentPool/members/add", { restourantIds: ids });
export const removePoolMembers = (ids: string[]) =>
  configWithUserToken.put<IPool>("CustomerIncidentPool/members/remove", { restourantIds: ids });

/* ───────── Hatalar ───────── */

const ERROR_TEXT: Record<string, string> = {
  CustomerIncidentsDisabled: "Olay bildirimi bu ortamda henüz açık değil.",
  CustomerIncidentForbidden: "Bu işlem için yetkiniz yok.",
  IncidentSubjectNotFound: "Sipariş bulunamadı ya da size ait değil.",
  IncidentRestaurantNotFound: "Restoran bulunamadı.",
  IncidentNotFound: "Bildirim bulunamadı (süresi dolmuş ya da kaldırılmış olabilir).",
  CustomerIncidentSubjectUnknown: "Bu siparişin müşterisi belirlenemiyor (hesap silinmiş).",
  IncidentAlreadyReported: "Bu siparişe bu türde bir bildirim zaten var.",
  IncidentTypeRequiresCancelledOrder: "Bu tür yalnız iptal edilmiş siparişe açılabilir.",
  IncidentNotEditable: "Bildirim bu durumdayken bu işlem yapılamaz.",
  ChangeRequestAlreadyOpen: "Bu bildirimde açık bir talep var; önce talebe karar verin.",
  ChangeRequestNotFound: "Talep bulunamadı.",
  ChangeRequestNotOpen: "Talep zaten kapanmış. Liste yenilendi.",
  EvidenceInvalid: "Görsel geçersiz: en fazla 5 MB, jpg/png/gif/webp.",
  EvidenceAlreadyAttached: "Bu bildirimin zaten bir görseli var.",
  EvidenceNotFound: "Bu bildirimin görseli yok.",
  EvidenceUploadFailed: "Görsel yüklenemedi, tekrar deneyin.",
  EvidenceDeleteFailed: "Görsel silinemedi; kayıt değişmedi. Tekrar deneyin.",
  PoolRestaurantNotFound: "Listede bulunamayan restoran var; hiçbir değişiklik uygulanmadı.",
};

export function incidentErrorCode(err: unknown): string | undefined {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  return Array.isArray(data) ? data[0]?.code : undefined;
}

export function incidentError(err: unknown, fallback: string): string {
  const e = err as { response?: { status?: number; data?: unknown } };
  const data = e?.response?.data;
  const first = Array.isArray(data) ? data[0] : null;
  if (first?.code && ERROR_TEXT[first.code]) return ERROR_TEXT[first.code];
  if (first?.description) return first.description;
  const errors = (data as { errors?: Record<string, string[]> })?.errors;
  if (errors) {
    const msg = Object.values(errors).flat()[0];
    if (msg) return msg;
  }
  if (e?.response?.status === 403) return "Bu işlem için yetkiniz yok.";
  return fallback;
}
